import { useMemo, useEffect, useState } from "react";
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid2 as Grid,
    IconButton,
    Stack,
    TableCell,
    Typography
} from "@mui/material";
import { BackHand, Edit, Event, Face, Flag, People, Public, Sell, SportsBaseball } from "@mui/icons-material";
import adService from "../../../../services/ad.service";
import { statratRed } from "../../../../config/constants";
import LoadingOverlay from "../../../Shared/LoadingOverlay";
import { ImpressionsWidget, CountryPlatformWidget } from "../../Widgets";
import { objectives } from "../../../../config/constants";
import { formatDate } from "../../../../hooks/formatNumber";
import CreateCampaign from "../../CreateCampaign";
import { MediaView } from "../../../Shared/MediaView";
import { Campaign, CampaignPerformance } from "../../../../types/campaign";

export const CampaignInfoCell = ({ ad }: { ad: Campaign}) => {
    const [campaignData, setCampaignData] = useState<CampaignPerformance | null>(null);
    const [openCampaignInfo, setOpenCampaignInfo] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const theme = useTheme();
    const isLightMode = theme.palette.mode === "light";

    const getAudienceFields = useMemo(() => {
        const fields = [];
        if (ad.ageMin && ad.ageMax) {
            fields.push({ label: `${ad.ageMin}-${ad.ageMax}yrs old`, icon: <Face /> });
        } else if (ad.ageMin) {
            fields.push({ label: `From ${ad.ageMin}yrs old`, icon: <Face /> });
        } else if (ad.ageMax) {
            fields.push({ label: `Up to ${ad.ageMax}yrs old`, icon: <Face /> });
        }
        if (ad.hittingHand) {
            fields.push({ label: ad.hittingHand === "RHB" ? "Right Hand Batting" : "Left Hand Batting", icon: <BackHand /> });
        }
        if (ad.throwingHand) {
            fields.push({ label: ad.hittingHand === "RHT" ? "Right Hand Throw" : "Left Hand Throw", icon: <SportsBaseball /> });
        }
        if (ad.positions) {
            ad.positions.map((position) => fields.push({ label: position }));
        }
        if (ad.country) {
            fields.push({ label: ad.country + (ad.state ? `, ${ad.state}` : "") + (ad.city ? `, ${ad.city}` : ""), icon: <Public /> });
        }
        if (fields.length === 0) {
            fields.push({ label: "Ad Targets All Users", icon: <People /> })
        }
        return fields;
    }, [ad]);

    const getDateRange = useMemo(() => {
        return `${formatDate(ad.startTime)} - ${formatDate(ad.endTime)}`
    }, [ad]);

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            const response = await adService.queryPerformance(ad.id, ad.startTime, ad.endTime);
            setIsLoading(false);
            setCampaignData(response);
        }
        if (openCampaignInfo) {
            fetchData();
        }
    }, [openCampaignInfo, ad.id, ad.startTime, ad.endTime]);

    return (
        <>
            <TableCell
                component="th"
                scope="row"
                onClick={() => setOpenCampaignInfo(true)}
                sx={{ cursor: "pointer" }}
            >
                <Typography color={statratRed}>{ad.nickname}</Typography>
                <Typography variant="caption" color={statratRed}>{ad.title}</Typography>
            </TableCell>
            <Dialog
                open={openCampaignInfo}
                onClose={() => { setOpenCampaignInfo(false) }}
                fullWidth={true}
                maxWidth="lg"
                aria-labelledby="campaign-info-title"
                aria-describedby="campaign-info-description"
            >
                {isLoading && <LoadingOverlay open={isLoading} /> }
                <DialogTitle id="campaign-info-title" sx={{ minWidth: 800 }}>
                    Campaign Info - {ad.nickname}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Flag fontSize="small" />
                        <Typography color="textSecondary">Objective: {objectives[ad.objective]}</Typography>
                        <Typography color="textSecondary">|</Typography>
                        <Sell fontSize="small" />
                        <Typography color="textSecondary">${ad.dailyBudget} per day</Typography>
                        <Typography color="textSecondary">|</Typography>
                        <Event fontSize="small" />
                        <Typography color="textSecondary">{getDateRange}</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ minWidth: 800 }}>
                    <Box width="100%" display="flex" flexDirection="column" gap={2}>
                        <Box position="relative" sx={{ backgroundColor: isLightMode ? "grey" : "black" }}>
                            <MediaView title="media-preview" urls={ad.media.urls} />
                            <Box
                                position="absolute"
                                bottom={0}
                                p={2}
                                width="100%"
                                display="flex"
                                justifyContent="space-between"
                                sx={{ backgroundColor: isLightMode ? 'rgba(211, 211, 211, 0.9)' : 'rgba(52, 52, 52, 0.9)' }}
                            >
                                <Box>
                                    <Typography variant="h6" alignSelf="flex-end">{ad.title}</Typography>
                                    <Typography variant="subtitle1" alignSelf="flex-end">{ad.headline}</Typography>
                                </Box>
                                <Typography alignSelf="flex-end">{ad.link}</Typography>
                            </Box>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            {getAudienceFields.map((field) => <Chip label={field.label} icon={field.icon} key={field.label} />)}
                        </Stack>
                        <Divider />
                        <Grid container spacing={2} width="100%">
                            <ImpressionsWidget
                                impressions={campaignData?.impressions ?? 0}
                                clicks={campaignData?.clicks ?? 0}
                                qualityImpressions={campaignData?.qualityImpressions ?? 0}
                                size={12}
                            />
                            <CountryPlatformWidget
                                countryData={campaignData?.countryImpressions ?? {}}
                                platformData={campaignData?.platformImpressions ?? {}}
                            />
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenCampaignInfo(false) }}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

type CampaignEditCellType = {
    ad: Campaign;
    fetchData: () => void;
    showEditButton?: boolean;
}

export const CampaignEditCell = ({ ad, fetchData, showEditButton = true }: CampaignEditCellType) => {
    const [openEditCampaign, setOpenEditCampaign] = useState(false);
    
    return (
        <>
            <TableCell component="th" scope="row">
                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                    {showEditButton && <>
                        <IconButton onClick={() => setOpenEditCampaign(true)}><Edit /></IconButton>
                        <CreateCampaign
                            fetchData={fetchData}
                            open={openEditCampaign}
                            setOpen={setOpenEditCampaign}
                            ad={ad}
                        />
                    </>}
                    <Box display="flex" flexDirection="column">
                        <Typography>{ad.nickname}</Typography>
                        <Typography variant="caption">{ad.title}</Typography>
                    </Box>
                </Box>
            </TableCell>
        </>
    )
}
